import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { IActionFormProps } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, Field, Label, SelectDropdown } from '@makyoapp/makyo-ui'

import { toasty } from '@/lib/toast'
import { useForm } from '@/lib/use-form'
import { toSdkOverlay } from '@/adapters/__project__/BuilderOverlayAdapter'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { isEmpty } from '@/shared/utils'
import { genNewOverlay } from '@/utils/builder/overlay'

export default function ActFormOpenOverlay({ onSubmit, onDismiss, initialData }: IActionFormProps) {
  const { overlays, appClient, updateVersion } = useAppBuilderContext()
  const overlayOptions = overlays?.filter((o) => !o.tableId) || []
  const { t } = useTranslation()
  const [pendingOverlay, setPendingOverlay] = useState<{ id: string; title: string; icon?: string } | null>(null)

  const { form, onSubmit: onFormSubmit } = useForm({
    defaultValues: {
      overlay: initialData?.overlay ?? overlays[0]?.id ?? '',
    },
    validate(values, setError) {
      if (!values.overlay?.trim().length) {
        setError('overlay', 'Please choose an overlay to display')
      }
    },
  })

  const handleSubmit = onFormSubmit((data) => {
    onSubmit(data)
  })

  const value = form.getValues('overlay')
  const invalidForm = isEmpty(value) || !!form.errors

  const handleCreateOverlay = async () => {
    try {
      const newOverlay = genNewOverlay(overlays || [], `Overlay ${(overlays ?? [])?.length + 1}`)
      setPendingOverlay({ id: newOverlay.id, title: newOverlay.title, icon: newOverlay.icon })
      form.setValues('overlay', newOverlay.id)
      await appClient.AddOverlay(toSdkOverlay(newOverlay))
      await updateVersion()
      toasty(t('New overlay created, modify later'), { type: 'success' })

      const persisted = Array.from(appClient.overlaysMap.values()).find(
        (ov: any) => ov?.settings?._Settings?._Title === newOverlay.title
      )
      if (persisted?.settings?._id) {
        form.setValues('overlay', persisted.settings._id)
      }
      setPendingOverlay(null)
    } catch (e) {
      console.error(e)
    }
  }

  const createOption = { id: '__create__', title: 'Create a new overlay', icon: 'add' as const }
  const baseOptions =
    pendingOverlay && !overlayOptions.find((o) => o.id === pendingOverlay.id)
      ? [pendingOverlay, ...overlayOptions]
      : overlayOptions
  const optionsWithCreate = [createOption, ...baseOptions]

  return (
    <div>
      <div className='p-4'>
        <Field>
          <Label required>Select Overlay</Label>

          <div className='flex-1'>
            {overlayOptions.length === 0 ? (
              <Button variant={ButtonVariant.Outlined} className='w-full justify-between' onClick={handleCreateOverlay}>
                <span className='flex items-center'>
                  <MaterialIcon size={20} icon='add' className='mr-2' />
                  <Trans>Create a new overlay</Trans>
                </span>
              </Button>
            ) : (
              <SelectDropdown
                multiple={false}
                options={optionsWithCreate}
                optionLabel={(opt) => opt.title}
                value={optionsWithCreate.find(({ id }) => id === form.getValues('overlay'))}
                renderOption={(opt) => {
                  if (opt?.id === '__create__') {
                    return (
                      <span className='flex items-center border border-dashed bdr rounded px-2 py-1 bg-secondary/10'>
                        <MaterialIcon size={20} icon='add' className='mr-2' />
                        <span>
                          <Trans>Create a new overlay</Trans>
                        </span>
                      </span>
                    )
                  }

                  return (
                    <span className='flex items-center'>
                      {opt?.icon && <MaterialIcon size={20} icon={opt.icon ?? 'info'} className='mr-2' />}
                      <span className={clsx(!opt?.id && 'text-light')}>{opt?.title}</span>
                    </span>
                  )
                }}
                onChange={(opt) => {
                  if (opt?.id === '__create__') {
                    handleCreateOverlay()
                    return
                  }
                  form.setValues('overlay', opt.id)
                }}
              />
            )}

            <div className='mt-1 text-m-sm red h-[17px]'>{form.errors?.overlay}</div>
          </div>
        </Field>
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
